import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Bell, ArrowLeft, ShoppingBag, Timer } from 'lucide-react';
import { pushToDataLayer, trackBeginCheckout } from '../lib/analytics';
import { useCountdown } from '../components/shop/DropCountdown';

const API = process.env.REACT_APP_BACKEND_URL;
const pad = (n) => String(n).padStart(2, '0');

const WomensDropHero = ({ drop }) => {
  const left = useCountdown(drop?.drops_at);
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle');
  if (!drop || !left) return null;

  const join = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState('sending');
    try {
      const res = await fetch(`${API}/api/leads/dojo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          magnet_id: 'womens_collection_waitlist',
          source: 'womens-collection-next-drop',
          metadata: { item: 'the full Women\u2019s Collection' },
        }),
      });
      if (!res.ok) throw new Error('failed');
      setState('done');
      pushToDataLayer('waitlist_join', {
        waitlist: 'womens_collection_next_drop',
        source: 'womens-collection-next-drop',
      });
    } catch {
      setState('error');
    }
  };

  const units = [
    [left.d, left.d === 1 ? 'Day' : 'Days'],
    [pad(left.h), 'Hrs'],
    [pad(left.m), 'Min'],
    [pad(left.s), 'Sec'],
  ];

  return (
    <div className="mt-10 border border-[#C7FA3E]/30 bg-[#0d1004] p-6 md:p-8" data-testid="womens-drop-hero">
      <p className="text-[#C7FA3E] text-xs uppercase tracking-[0.25em] mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
        <Timer className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
        Drops in · {drop.title}
      </p>
      <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
        <div className="flex gap-4 md:gap-6" data-testid="womens-drop-countdown">
          {units.map(([val, label]) => (
            <div key={label} className="text-center">
              <div className="text-white text-4xl md:text-5xl leading-none" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{val}</div>
              <div className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] mt-1.5">{label}</div>
            </div>
          ))}
        </div>
        <div className="flex-1 md:max-w-md">
          {state === 'done' ? (
            <p className="inline-flex items-center gap-2 text-[#C7FA3E] text-xs uppercase tracking-widest" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="womens-drop-waitlist-success">
              <Check className="w-4 h-4" /> You&apos;re on the list — first access secured.
            </p>
          ) : (
            <>
              <form onSubmit={join} className="flex gap-2" data-testid="womens-drop-waitlist-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  disabled={state === 'sending'}
                  className="flex-1 min-w-0 px-4 py-2.5 bg-black/50 border border-zinc-700 text-white text-sm focus:outline-none focus:border-[#C7FA3E] transition-colors"
                  data-testid="womens-drop-waitlist-email"
                />
                <button
                  type="submit"
                  disabled={state === 'sending'}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#C7FA3E] hover:bg-[#b8e01f] disabled:opacity-60 text-black text-xs font-bold uppercase tracking-widest transition-colors whitespace-nowrap"
                  data-testid="womens-drop-waitlist-submit"
                >
                  <Bell className="w-3.5 h-3.5" /> {state === 'sending' ? 'Joining…' : 'Get first access'}
                </button>
              </form>
              {state === 'error' && (
                <p className="text-red-400 text-xs mt-2" data-testid="womens-drop-waitlist-error">Something went wrong — try again.</p>
              )}
              <p className="text-zinc-500 text-xs mt-2">One email when it drops. First access before anyone else.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const PieceCard = ({ item }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle');
  const [size, setSize] = useState('M');
  const [buying, setBuying] = useState(false);

  const buy = async () => {
    setBuying(true);
    pushToDataLayer('womens_collection_buy_click', {
      item: item.name,
      item_key: item.key,
      size,
      price: item.price,
      currency: 'USD',
    });
    trackBeginCheckout(item.price, [{
      item_id: `womens-${item.key}`,
      item_name: item.name,
      item_variant: size,
      price: item.price,
      quantity: 1,
    }], 'womens_collection');
    try {
      const res = await fetch(`${API}/api/womens-collection/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: item.key, size, quantity: 1 }),
      });
      if (!res.ok) throw new Error('failed');
      const { url } = await res.json();
      window.location.href = url;
    } catch {
      setBuying(false);
      setState('error');
    }
  };

  const join = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState('sending');
    try {
      const res = await fetch(`${API}/api/leads/dojo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          magnet_id: 'womens_collection_waitlist',
          source: `womens-${item.key}`,
          metadata: { item: item.name },
        }),
      });
      if (!res.ok) throw new Error('failed');
      setState('done');
      pushToDataLayer('waitlist_join', {
        waitlist: 'womens_collection',
        item: item.name,
        item_key: item.key,
        source: `womens-${item.key}`,
      });
    } catch {
      setState('error');
    }
  };

  return (
    <div className="group bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors flex flex-col" data-testid={`womens-item-card-${item.key}`}>
      <div className="relative aspect-square overflow-hidden bg-zinc-900">
        <img
          src={item.image.startsWith('http') ? item.image : `${API}${item.image}`}
          alt={`${item.name} — NinjAthlete Women's Collection`}
          className={`w-full h-full object-cover transition-opacity duration-300 ${item.back_image ? 'group-hover:opacity-0' : 'group-hover:scale-105'}`}
          loading="lazy"
        />
        {item.back_image && (
          <img
            src={item.back_image.startsWith('http') ? item.back_image : `${API}${item.back_image}`}
            alt={`${item.name} — front view`}
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            loading="lazy"
            data-testid={`womens-item-back-${item.key}`}
          />
        )}
        <span className="absolute top-3 left-3 px-2 py-1 text-[10px] uppercase tracking-widest bg-black/70 text-[#C7FA3E] border border-[#C7FA3E]/30" style={{ fontFamily: 'Oswald, sans-serif' }}>
          Drop 02
        </span>
        {item.waitlist_count > 0 && (
          <span className="absolute top-3 right-3 px-2 py-1 text-[10px] uppercase tracking-wider bg-black/90 text-white border border-zinc-600" data-testid={`womens-item-count-${item.key}`}>
            {item.waitlist_count} on the list
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-white text-xl leading-tight mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
          {item.name}
        </h3>
        <p className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] mb-2">{item.subtitle} · Limited Run</p>
        <p className="text-zinc-400 text-xs leading-relaxed mb-3">{item.description}</p>
        <p className="text-zinc-300 text-sm mb-4 mt-auto">
          <span className="text-white font-semibold">${item.price.toFixed(0)}</span>
          <span className="text-zinc-500"> · drop price · {item.sizes[0]}–{item.sizes[item.sizes.length - 1]}</span>
        </p>

        <div>
          {item.available ? (
            <div data-testid={`womens-item-buy-${item.key}`}>
              <div className="flex gap-1.5 mb-3 flex-wrap">
                {item.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    className={`px-2.5 py-1.5 text-xs font-bold border transition-colors ${size === s ? 'bg-[#C7FA3E] text-black border-[#C7FA3E]' : 'text-zinc-400 border-zinc-700 hover:border-zinc-500'}`}
                    style={{ fontFamily: 'Oswald, sans-serif' }}
                    data-testid={`womens-size-${item.key}-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <button
                onClick={buy}
                disabled={buying}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#C7FA3E] hover:bg-[#b8e01f] disabled:opacity-60 text-black text-xs font-bold uppercase tracking-widest transition-colors"
                style={{ fontFamily: 'Oswald, sans-serif' }}
                data-testid={`womens-buy-btn-${item.key}`}
              >
                <ShoppingBag className="w-3.5 h-3.5" /> {buying ? 'Opening checkout…' : `Buy · $${item.price.toFixed(0)}`}
              </button>
            </div>
          ) : state === 'done' ? (
            <p className="inline-flex items-center gap-2 text-[#C7FA3E] text-xs uppercase tracking-widest" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid={`womens-success-${item.key}`}>
              <Check className="w-4 h-4" /> You&apos;re on the list
            </p>
          ) : open ? (
            <form onSubmit={join} className="flex gap-2" data-testid={`womens-form-${item.key}`}>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                autoComplete="email"
                className="flex-1 min-w-0 bg-black/50 border border-zinc-700 focus:border-[#C7FA3E] px-3 py-2.5 text-sm text-white outline-none transition-colors"
                data-testid={`womens-email-${item.key}`}
              />
              <button
                type="submit"
                disabled={state === 'sending'}
                className="px-4 py-2.5 bg-[#C7FA3E] hover:bg-[#b8e01f] disabled:opacity-60 text-black text-xs font-bold uppercase tracking-widest transition-colors whitespace-nowrap"
                style={{ fontFamily: 'Oswald, sans-serif' }}
                data-testid={`womens-submit-${item.key}`}
              >
                {state === 'sending' ? '…' : 'Join'}
              </button>
            </form>
          ) : (
            <button
              onClick={() => setOpen(true)}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#C7FA3E]/40 text-[#C7FA3E] hover:bg-[#C7FA3E] hover:text-black text-xs font-bold uppercase tracking-widest transition-colors"
              style={{ fontFamily: 'Oswald, sans-serif' }}
              data-testid={`womens-waitlist-btn-${item.key}`}
            >
              <Bell className="w-3.5 h-3.5" /> Join the waitlist
            </button>
          )}
          {state === 'error' && (
            <p className="text-red-400 text-xs mt-2" data-testid={`womens-error-${item.key}`}>Something went wrong — try again.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default function WomensCollectionPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Women's Collection — Muscle Mommy Tee, Sports Bra & Shorts (Drop 02) | NinjAthlete";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute('content');
    meta?.setAttribute('content', "The first NinjAthlete Women's Collection — Muscle Mommy oversized tee, strappy sports bra and high-waist yoga shorts. Limited drop, waitlist open.");
    fetch(`${API}/api/womens-collection`).then((r) => r.json()).then(setData).catch(() => {});
    return () => {
      document.title = 'NinjAthlete';
      if (prevDesc) meta?.setAttribute('content', prevDesc);
    };
  }, []);

  useEffect(() => {
    if (!data?.items) return undefined;
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: "NinjAthlete Women's Collection — Drop 02",
      itemListElement: data.items.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Product',
          name: p.name,
          description: p.description,
          image: p.image.startsWith('http') ? p.image : `https://ninjathlete.com${p.image}`,
          brand: { '@type': 'Brand', name: 'NinjAthlete' },
          offers: {
            '@type': 'Offer',
            price: p.price.toFixed(2),
            priceCurrency: 'USD',
            availability: p.available ? 'https://schema.org/InStock' : 'https://schema.org/PreOrder',
            url: 'https://ninjathlete.com/womens-collection',
          },
        },
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'womens-collection-schema';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => document.getElementById('womens-collection-schema')?.remove();
  }, [data]);

  return (
    <main className="pt-20 min-h-screen bg-[#050505]" data-testid="womens-collection-page">
      <section className="container-ninja section-spacing">
        <Link to="/shop" className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-white text-xs uppercase tracking-widest mb-8 transition-colors" data-testid="womens-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to shop
        </Link>
        <div className="max-w-3xl">
          <p className="text-ninja-label text-[#C7FA3E] mb-4">
            {data?.status === 'live' ? 'Drop 02 · Live now' : 'Drop 02 · Waitlist open'}
          </p>
          <h1 className="text-white mb-6">
            Women&apos;s <span className="text-gradient-ninja">Collection</span>
          </h1>
          <p className="text-zinc-400 text-lg leading-relaxed mb-3">
            The first-ever NinjAthlete women&apos;s line — the Muscle Mommy oversized tee, a strappy
            racerback sports bra and high-waist yoga shorts. Built for the
            women who lift, run and never back down.
          </p>
          <p className="text-zinc-500 text-sm">
            Waitlist gets first access and drop-day pricing before anyone else. No spam — one email when it drops.
          </p>
          {data?.waitlist_total > 0 && (
            <p className="text-[#C7FA3E] text-xs uppercase tracking-widest mt-4" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="womens-collection-total">
              {data.waitlist_total} {data.waitlist_total === 1 ? 'athlete' : 'athletes'} already on the list
            </p>
          )}
          <WomensDropHero drop={data?.next_drop} />
        </div>
      </section>

      <section className="container-ninja pb-16">
        {!data ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#C7FA3E] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="womens-collection-grid">
            {data.items.map((item) => <PieceCard key={item.key} item={item} />)}
          </div>
        )}
      </section>

      {data?.lifestyle?.length > 0 && (
        <section className="container-ninja pb-20" data-testid="womens-lifestyle-section">
          <p className="text-ninja-label text-zinc-500 mb-2">Worn by the crew</p>
          <h2 className="text-white text-2xl md:text-3xl mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
            From the gym floor to the HYROX floor
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {data.lifestyle.map((img) => (
              <div key={img.src} className="overflow-hidden border border-zinc-800 bg-zinc-900" data-testid="womens-lifestyle-photo">
                <img
                  src={img.src.startsWith('http') ? img.src : `${API}${img.src}`}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full max-h-[560px] object-cover object-top hover:scale-[1.02] transition-transform duration-700"
                />
              </div>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
